import { useState } from "react";
import { refusalOf } from "./answer";
import { rowResults, useActivities, useReplaySms } from "./control";
import {
  readReplayWindow,
  receivedTime,
  replayComparison,
  replayWindowFor,
  signedGap,
  smsInWindow,
  type ReplayCount,
  type SmsMessage,
} from "./sms";
import { SMS_VOTE_TYPES, type LivePayload, type Results } from "./types";
import { Button } from "../ui/Button";
import { Confirm } from "../ui/Confirm";

const KIND: Record<string, string> = { poll: "Sondage", quiz: "Quiz" };

const plural = (n: number, one: string, many: string) =>
  `${n} ${n > 1 ? many : one}`;

/** Le jour et l'heure à la seconde : une plage peut enjamber minuit. */
const moment = (iso: string) =>
  `${new Date(iso).toLocaleDateString("fr-FR")} à ${receivedTime(iso)}`;

/** Une case du tableau avant / après : la part, puis les voix, comme `Tally`. */
const cell = ({ votes, share }: ReplayCount) => `${Math.round(share * 100)}% (${votes})`;

/**
 * ═══ REJOUER LES VOTES PAR SMS D'UNE ACTIVITÉ ═══
 *
 * Une activité, une plage horaire, et les votes SMS de l'activité sont effacés puis
 * recomptés depuis les SMS reçus dans la plage (voir `sms.ts`). La plage proposée est
 * celle où l'activité était ouverte ; on la corrige quand les SMS sont arrivés en
 * dehors — trop tôt, trop tard, ou pendant qu'une autre activité était à l'écran.
 *
 * ⚠️ ÇA EFFACE, donc ça se confirme, comme « Vider les réponses ». La question dit ce
 * qui part (les votes SMS de CETTE activité) et ce qui reste (ceux de l'application).
 *
 * Une fois rejoué, les résultats d'avant et d'après, réponse par réponse (#333) :
 * ceux d'avant sont gardés à la confirmation, ceux d'après sont ceux de la régie.
 *
 * Displayed strings are French: this is a French product.
 */
export function SmsReplay({
  id,
  hidden,
  live,
  messages,
  total,
}: {
  id: string;
  hidden: boolean;
  /** Le direct : il porte les résultats les plus frais de l'activité à l'écran. */
  live: Pick<LivePayload, "activity" | "results">;
  messages: SmsMessage[];
  total: number;
}) {
  const { data: activities = [] } = useActivities();
  const replay = useReplaySms();
  const votable = activities.filter((a) => SMS_VOTE_TYPES.includes(a.type));

  const [activityId, setActivityId] = useState<number | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [checked, setChecked] = useState(false);
  const [confirm, setConfirm] = useState(false);
  /* Les résultats au moment de confirmer : le rejeu les efface, rien ne les redonne. */
  const [before, setBefore] = useState<{
    activityId: number;
    results: Results | null;
  } | null>(null);

  const activity = votable.find((a) => a.id === activityId);
  const name = activity ? activity.title || activity.content || "Sans titre" : "";
  const typed = readReplayWindow(from, to);
  const range = "error" in typed ? null : typed;

  const comparison =
    replay.data && activity && before?.activityId === activity.id
      ? replayComparison(activity.choices, before.results, rowResults(activity, live))
      : null;

  /* ⚠️ UN MINIMUM QUAND LA LISTE NE PORTE PAS TOUT : le serveur, lui, les lit tous. */
  const inRange = range ? smsInWindow(messages, range) : 0;
  const counted = `${total > messages.length ? "au moins " : ""}${plural(
    inRange,
    "SMS reçu",
    "SMS reçus",
  )}`;

  function choose(next: number | null) {
    setActivityId(next);
    setChecked(false);
    replay.reset();
    const chosen = votable.find((a) => a.id === next);
    if (!chosen) return;
    const proposed = replayWindowFor(chosen, new Date());
    setFrom(proposed.from);
    setTo(proposed.to);
  }

  return (
    <div
      className="smsr"
      id={id}
      hidden={hidden}
      role="group"
      aria-label="Rejouer les votes d'une activité"
    >
      <p className="hint">
        Les votes SMS de l'activité sont effacés, puis recomptés depuis les SMS reçus
        entre le début et la fin, qu'elle ait été à l'écran ou non : le premier SMS de
        chaque numéro qui porte un numéro de réponse compte. Les votes faits depuis
        l'application ne changent pas.
      </p>

      {votable.length === 0 ? (
        <p className="empty">
          Aucun sondage ni quiz sur cet événement : rien ne se vote par SMS.
        </p>
      ) : (
        <>
          <div className="f">
            <label htmlFor={`${id}-activite`}>Activité</label>
            <select
              id={`${id}-activite`}
              value={activityId ?? ""}
              onChange={(e) => choose(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Choisir une activité</option>
              {votable.map((a) => (
                <option key={a.id} value={a.id}>
                  {`${a.title || a.content || "Sans titre"} (${KIND[a.type] ?? a.type})`}
                </option>
              ))}
            </select>
          </div>

          <div className="smsr__plage">
            <div className="f">
              <label htmlFor={`${id}-debut`}>Début</label>
              <input
                id={`${id}-debut`}
                type="datetime-local"
                step={1}
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  replay.reset();
                }}
              />
            </div>
            <div className="f">
              <label htmlFor={`${id}-fin`}>Fin</label>
              <input
                id={`${id}-fin`}
                type="datetime-local"
                step={1}
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  replay.reset();
                }}
              />
            </div>
          </div>

          {range ? <p className="hint">{counted} sur cette plage.</p> : null}

          {checked && "error" in typed ? (
            <p className="live__err" role="alert">
              {typed.error}
            </p>
          ) : null}

          {replay.isError ? (
            <p className="live__err" role="alert">
              {refusalOf(replay.error)}
            </p>
          ) : null}

          {replay.data ? (
            <p className="hint" role="status">
              {`${plural(replay.data.voters, "vote rejoué", "votes rejoués")} depuis ${
                replay.data.messages
              } SMS ; ${plural(
                replay.data.deletedVoters,
                "vote SMS effacé",
                "votes SMS effacés",
              )} avant le rejeu.`}
            </p>
          ) : null}

          {comparison ? (
            <table className="smsr__cmp">
              <caption>{`Résultats de « ${name} »`}</caption>
              <thead>
                <tr>
                  <th scope="col">Réponse</th>
                  <th scope="col">Avant</th>
                  <th scope="col">Après</th>
                  <th scope="col">Écart</th>
                </tr>
              </thead>
              <tbody>
                {comparison.rows.map((row) => (
                  <tr key={row.choiceId}>
                    <th scope="row">
                      <span className="smsn">{row.number}</span>
                      {row.label}
                    </th>
                    <td>{cell(row.before)}</td>
                    <td>{cell(row.after)}</td>
                    <td>{signedGap(row.before.votes, row.after.votes)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Réponses</th>
                  <td>{comparison.votes.before}</td>
                  <td>{comparison.votes.after}</td>
                  <td>{signedGap(comparison.votes.before, comparison.votes.after)}</td>
                </tr>
                {comparison.voters.before !== null && comparison.voters.after !== null ? (
                  <tr>
                    <th scope="row">Votants</th>
                    <td>{comparison.voters.before}</td>
                    <td>{comparison.voters.after}</td>
                    <td>{signedGap(comparison.voters.before, comparison.voters.after)}</td>
                  </tr>
                ) : null}
              </tfoot>
            </table>
          ) : null}

          <Button
            disabled={!activity}
            loading={replay.isPending}
            onClick={() => {
              setChecked(true);
              if (range) setConfirm(true);
            }}
          >
            Rejouer les votes
          </Button>
        </>
      )}

      <Confirm
        open={confirm && !!activity && !!range}
        title="Rejouer les votes SMS ?"
        message={
          range
            ? `Les votes SMS de « ${name} » seront effacés définitivement, puis recomptés depuis les SMS reçus du ${moment(
                range.from,
              )} au ${moment(range.to)} (${counted}). Les votes faits depuis l'application sont conservés.`
            : ""
        }
        confirmLabel="Rejouer les votes"
        onConfirm={() => {
          if (!activity || !range) return;
          setBefore({ activityId: activity.id, results: rowResults(activity, live) });
          replay.mutate(
            { activityId: activity.id, ...range },
            { onSettled: () => setConfirm(false) },
          );
        }}
        busy={replay.isPending}
        onCancel={() => setConfirm(false)}
        emphasis="cancel"
      />
    </div>
  );
}
